import { logger } from './logger';

// Create component logger for localStorage utilities
const localStorageLogger = logger.withComponent('LocalStorage');

/**
 * Prefix for all core setup related localStorage keys
 */
export const CORE_SETUP_PREFIX = 'core_setup';

/**
 * Core setup localStorage key patterns
 */
export const CORE_SETUP_KEYS = {
  SESSION_ID: 'core_setup_session_id',
  DEVICE_SERIAL_NUMBER: 'core_setup_device_serial_number',
  DEVICE_MODEL: 'core_setup_device_model',
  DERIVED_MODEL: 'core_setup_derived_model', // Original model from serialToModel (PRO_AI, MOBILE, etc.)
  DEVICE_SKU: 'core_setup_device_sku',
} as const;

/**
 * Clear all localStorage items that start with a specific prefix
 * @param prefix - The prefix to match (default: 'core_setup')
 * @returns Number of items cleared
 */
export const clearLocalStorageByPrefix = (prefix: string = CORE_SETUP_PREFIX): number => {
  try {
    const keysToRemove: string[] = [];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove the identified keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      localStorageLogger.debug(`Removed localStorage key: ${key}`);
    });
    
    localStorageLogger.info(`Cleared ${keysToRemove.length} localStorage items with prefix: ${prefix}`, {
      prefix,
      keysCleared: keysToRemove,
      count: keysToRemove.length
    });
    
    return keysToRemove.length;
  } catch (error) {
    localStorageLogger.error('Failed to clear localStorage by prefix', {
      prefix,
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    return 0;
  }
};

/**
 * Clear all core setup related localStorage items
 * @returns Number of items cleared
 */
export const clearCoreSetupLocalStorage = (): number => {
  return clearLocalStorageByPrefix(CORE_SETUP_PREFIX);
};

/**
 * Clear specific core setup localStorage keys
 * @param keys - Array of specific keys to clear
 * @returns Number of items cleared
 */
export const clearSpecificCoreSetupKeys = (keys: string[]): number => {
  try {
    let clearedCount = 0;
    
    keys.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        clearedCount++;
        localStorageLogger.debug(`Removed specific localStorage key: ${key}`);
      }
    });
    
    localStorageLogger.info(`Cleared ${clearedCount} specific localStorage keys`, {
      keysRequested: keys,
      keysCleared: clearedCount
    });
    
    return clearedCount;
  } catch (error) {
    localStorageLogger.error('Failed to clear specific localStorage keys', {
      keys,
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    return 0;
  }
};

/**
 * Get all localStorage keys that start with core_setup prefix
 * @returns Array of matching keys
 */
export const getCoreSetupLocalStorageKeys = (): string[] => {
  const keys: string[] = [];
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CORE_SETUP_PREFIX)) {
        keys.push(key);
      }
    }
  } catch (error) {
    localStorageLogger.error('Failed to get core setup localStorage keys', error);
  }
  
  return keys;
};

/**
 * Check if any core setup localStorage items exist
 * @returns True if any core setup items exist
 */
export const hasCoreSetupLocalStorage = (): boolean => {
  return getCoreSetupLocalStorageKeys().length > 0;
};

/**
 * Get stored device information from localStorage
 * @returns Object with stored serial and sku, or null values if not found
 */
export const getStoredDeviceInfo = (): { serial: string | null; sku: string | null } => {
  try {
    const getStoredValue = (key: string): string | null => {
      const item = localStorage.getItem(key);
      if (!item) return null;
      
      try {
        const parsed = JSON.parse(item);
        return parsed.value || null;
      } catch {
        // If not JSON, return the raw value (for backward compatibility)
        return item;
      }
    };

    const serial = getStoredValue(CORE_SETUP_KEYS.DEVICE_SERIAL_NUMBER);
    const sku = getStoredValue(CORE_SETUP_KEYS.DEVICE_SKU);

    localStorageLogger.debug('Retrieved stored device info', { serial, sku });

    return { serial, sku };
  } catch (error) {
    localStorageLogger.error('Failed to get stored device info', error);
    return { serial: null, sku: null };
  }
};

/**
 * Check if current device parameters match stored values
 * @param currentSerial - Current serial number from URL/params
 * @param currentSku - Current SKU from URL/params
 * @returns True if values match or if no stored values exist
 */
export const doesDeviceInfoMatch = (
  currentSerial: string, 
  currentSku?: string
): boolean => {
  const stored = getStoredDeviceInfo();
  
  // If no stored values, consider it a match (fresh start)
  if (!stored.serial && !stored.sku) {
    localStorageLogger.debug('No stored device info found, considering as match');
    return true;
  }

  // Check serial number match (required)
  const serialMatches = stored.serial === currentSerial;
  
  // Check SKU match (optional, only if both exist)
  const skuMatches = !currentSku || !stored.sku || stored.sku === currentSku;

  const matches = serialMatches && skuMatches;

  localStorageLogger.info('Device info match check', {
    currentSerial,
    currentSku,
    storedSerial: stored.serial,
    storedSku: stored.sku,
    serialMatches,
    skuMatches,
    overallMatch: matches
  });

  return matches;
};

/**
 * Clear localStorage if device parameters don't match stored values
 * @param currentSerial - Current serial number from URL/params
 * @param currentSku - Current SKU from URL/params
 * @returns Object with match status and number of items cleared
 */
export const clearLocalStorageOnDeviceMismatch = (
  currentSerial: string,
  currentSku?: string
): { matched: boolean; clearedCount: number } => {
  const matched = doesDeviceInfoMatch(currentSerial, currentSku);
  
  if (!matched) {
    localStorageLogger.info('Device mismatch detected, clearing localStorage', {
      currentSerial,
      currentSku,
      reason: 'Device parameters do not match stored values'
    });
    
    const clearedCount = clearCoreSetupLocalStorage();
    return { matched: false, clearedCount };
  }

  localStorageLogger.debug('Device info matches, no localStorage clearing needed');
  return { matched: true, clearedCount: 0 };
};
